<template>
  <div>
    <small
      class="align-items-center"
      :class="{
        'text-success': progress.successful,
        'text-danger': progress.successful === false,
      }"
    >
      <div v-if="title" style="font-size: 1.2em">
        <i v-if="progress.successful === false" class="fa fa-times" />
        <template v-else>
          <i v-if="progress.finished" class="fa fa-check" />
          <i v-else class="spinner spinner-border spinner-border-sm refresh-spinner" />
        </template>

        <span
          class="ml-1"
          :class="{
            'line-through': progress.successful === false,
          }"
        >
          {{ title }}
        </span>
      </div>

      <slot v-if="slots.before" name="before" />

      <span v-for="(log, i) of progress.progressLog" :key="i" class="d-block">
        <!-- <i class="fa fa-check" /> -->
        {{ log }} ...done
      </span>

      <template v-if="progress.currentAction">
        <span v-if="progress.currentAction" class="current-action mr-1 overflow-hidden">
          {{ progress.currentAction }}
        </span>
      </template>

      <hr v-if="'children' in progress && progress.finished" class="m-0 w-33" />

      <!-- Bar -->
      <template v-if="progress.percentage !== undefined && !progress.finished">
        <div :key="progress.currentAction" class="ml-2 visual-progress-bar" style="margin-bottom: -2px">
          <div
            class="inner-bar"
            :style="`width: ${progress.percentage}%; background-color: ${progress.successful === false ? 'var(--danger)' : 'var(--success)'}`"
          />
        </div>
        <span class="ml-2"> {{ Math.round(progress.percentage) }}% </span>
      </template>

      <!-- Results -->
      <span v-for="(arr, progressAction) in indexedUniqueEntries" :key="progressAction" class="progress-action">
        <!-- Hover popup -->
        <TooltipComponent direction="up" :disabled="!arr.length" :tooltip-style="{ padding: 0 }">
          <template #trigger>
            <span :class="{ 'font-weight-bold': arr.length }"> {{ translate(progressAction, 'application') }}: {{ arr.length }} </span>
          </template>
          <div style="overflow: hidden">
            <div class="scroll-box p-2">
              <div v-for="(entry, j) of arr" :key="j">
                {{ entry?.sku }}
                {{ entry?.reason }}
              </div>
            </div>
            <div class="px-2 py-1">
              <a :href="`/products?_field_sku=${arr.map((p) => p.sku)?.join(',')}`" :target="`${progressAction}-${progress.id}`">
                Visa artiklar i extern flik
                <i class="fa fa-external-link" />
              </a>
            </div>
          </div>
        </TooltipComponent>
      </span>
    </small>

    <slot />
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from '@/composables/useTranslation'
import { ChildProgress, Progress } from '@/models/Global'
import { computed, useSlots } from 'vue'
import TooltipComponent from '../simple/TooltipComponent.vue'
import { filterUniqueEntries } from '@/utils/tools'

type Entry = { sku: string; reason?: string }

type Report = Record<string, Entry[]>

const props = defineProps<{ progress: Progress<Report> | ChildProgress<Report>; title?: string }>()

const slots = useSlots()
const { translate } = useTranslation()

const indexedUniqueEntries = computed(() => {
  const indexedData: { [action: string]: Entry[] } = {}

  for (const action in props.progress.data) {
    indexedData[action] = filterUniqueEntries(props.progress.data[action], 'sku')
  }

  return indexedData
})
</script>

<style lang="scss" scoped>
.current-action {
  word-break: break-word;
  max-height: 100px;
  max-width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.progress-action:not(:last-child) {
  &::after {
    content: ', ';
  }
}

.visual-progress-bar {
  display: inline-block;
  border: 1px solid #ccc;
  height: 1em;
  width: 200px;

  .inner-bar {
    transition: width 0.5s;
    height: 100%;
  }
}

.scroll-box {
  overflow: auto;
  max-height: 400px;
}
</style>
